import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  cmToFeetInches,
  feetInchesToCm,
  formatFtIn,
  bdHeightPercentile,
  BD_AVG,
  GLOBAL_AVG,
  HEIGHT_TABLE_CM,
  type FeetInches,
} from "@/lib/calculators/cmToFeet";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type Direction = "cmToFt" | "ftToCm";

const T = {
  h1: { en: "CM to Feet Converter", bn: "সেমি থেকে ফুট কনভার্টার" },
  desc: {
    en: "Convert centimeters to feet & inches instantly — height-friendly, accurate to 0.01\". Reverse mode (feet → cm), Bengali numerals, and BD average-height reference.",
    bn: "সেন্টিমিটার থেকে ফুট-ইঞ্চিতে তাৎক্ষণিক রূপান্তর — ০.০১\" পর্যন্ত সঠিক। ফুট → সেমি মোড, বাংলা সংখ্যা এবং বাংলাদেশের গড় উচ্চতা রেফারেন্স সহ।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  cmToFt: { en: "CM → Feet", bn: "সেমি → ফুট" },
  ftToCm: { en: "Feet → CM", bn: "ফুট → সেমি" },
  cm: { en: "Centimeters", bn: "সেন্টিমিটার" },
  feet: { en: "Feet", bn: "ফুট" },
  inches: { en: "Inches", bn: "ইঞ্চি" },
  result: { en: "Result", bn: "ফলাফল" },
  swap: { en: "Swap direction", bn: "দিক বদল" },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "What is 165 cm in feet?", bn: "১৬৫ সেন্টিমিটার কত ফুট?" },
    a: { en: "165 cm = 5 feet 5 inches (5'5\"), or precisely 5'4.96\" — 5.41 feet, 64.96 inches, 1.65 m.",
         bn: "১৬৫ সেমি = ৫ ফুট ৫ ইঞ্চি (৫'৫\"); সঠিকভাবে ৫'৪.৯৬\" — ৫.৪১ ফুট, ৬৪.৯৬ ইঞ্চি, ১.৬৫ মিটার।" } },
  { q: { en: "What is 5 feet 7 inches in cm?", bn: "৫ ফুট ৭ ইঞ্চি কত সেন্টিমিটার?" },
    a: { en: "5'7\" = (5 × 12 + 7) × 2.54 = 67 × 2.54 = 170.18 cm.",
         bn: "৫'৭\" = (৫ × ১২ + ৭) × ২.৫৪ = ৬৭ × ২.৫৪ = ১৭০.১৮ সেমি।" } },
  { q: { en: "How tall is 170 cm in feet?", bn: "১৭০ সেমি কত ফুট?" },
    a: { en: "170 cm ≈ 5 feet 7 inches (precisely 5'6.93\"). It's slightly above the Bangladesh male average (163.8 cm).",
         bn: "১৭০ সেমি ≈ ৫ ফুট ৭ ইঞ্চি (সঠিকভাবে ৫'৬.৯৩\")। বাংলাদেশের পুরুষদের গড় উচ্চতা (১৬৩.৮ সেমি)-এর চেয়ে সামান্য বেশি।" } },
  { q: { en: "How is the conversion done?", bn: "কীভাবে রূপান্তর করা হয়?" },
    a: { en: "1 inch = 2.54 cm exactly. So cm ÷ 2.54 = total inches; divide by 12 for feet, remainder is the inches part. Reverse: (feet × 12 + inches) × 2.54 = cm.",
         bn: "১ ইঞ্চি = ২.৫৪ সেমি (সঠিক)। সুতরাং সেমি ÷ ২.৫৪ = মোট ইঞ্চি; ১২ দিয়ে ভাগ করলে ফুট, অবশিষ্ট ইঞ্চি। বিপরীতে: (ফুট × ১২ + ইঞ্চি) × ২.৫৪ = সেমি।" } },
  { q: { en: "What's the average height in Bangladesh?", bn: "বাংলাদেশে গড় উচ্চতা কত?" },
    a: { en: "Per BBS / WHO data, average adult male ≈ 163.8 cm (5'4\"); average adult female ≈ 151.8 cm (5'0\"). Younger generations are slightly taller.",
         bn: "BBS / WHO অনুযায়ী, প্রাপ্তবয়স্ক পুরুষের গড় ≈ ১৬৩.৮ সেমি (৫'৪\"); নারীর গড় ≈ ১৫১.৮ সেমি (৫'০\")। তরুণ প্রজন্ম কিছুটা লম্বা।" } },
  { q: { en: "Which format do BD passports / NID use?", bn: "পাসপোর্ট / NID কোন ফরম্যাটে?" },
    a: { en: "Bangladesh passports record height in centimeters; NID uses cm; medical and biodata forms often use feet & inches. This converter shows all formats so you can fill any form.",
         bn: "বাংলাদেশের পাসপোর্টে উচ্চতা সেন্টিমিটারে; NID-তেও সেমি; মেডিকেল ও বায়োডাটায় ফুট-ইঞ্চি। এই কনভার্টার সব ফরম্যাট দেখায়।" } },
];

export const Route = createFileRoute("/cm-to-feet")({
  head: () => {
    const seo = buildSeo({
      title: "CM to Feet Converter — Height in Feet & Inches",
      description:
        "Free CM to feet converter. Convert centimeters to feet and inches instantly. Height conversion for Bangladesh — accurate and fast. সেমি থেকে ফুট কনভার্টার।",
      path: "/cm-to-feet",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "CM to Feet", path: "/cm-to-feet" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: CmToFeetPage,
});

const QUICK_CHIPS = [
  { ft: 5, in: 0 }, { ft: 5, in: 5 }, { ft: 5, in: 7 }, { ft: 5, in: 10 }, { ft: 6, in: 0 },
];

function stripBn(s: string): string {
  return s.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
}

function CmToFeetPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 2) => {
    if (!Number.isFinite(n)) return "—";
    const s = (Math.round(n * 10 ** d) / 10 ** d).toLocaleString("en-US", { maximumFractionDigits: d });
    return useBn ? toBengaliNumerals(s) : s;
  };
  const fmtFtIn = (f: FeetInches) => useBn ? toBengaliNumerals(formatFtIn(f)) : formatFtIn(f);
  const fmtFtInPrecise = (f: FeetInches) => useBn ? toBengaliNumerals(formatFtIn(f, true)) : formatFtIn(f, true);

  const [dir, setDir] = useState<Direction>("cmToFt");
  const [cmStr, setCmStr] = useState("165");
  const [ftStr, setFtStr] = useState("5");
  const [inStr, setInStr] = useState("7");

  const result: FeetInches = useMemo(() => {
    if (dir === "cmToFt") return cmToFeetInches(parseFloat(cmStr) || 0);
    return feetInchesToCm(parseFloat(ftStr) || 0, parseFloat(inStr) || 0);
  }, [dir, cmStr, ftStr, inStr]);

  const swap = () => {
    if (dir === "cmToFt") {
      // carry current cm into ft+in
      setFtStr(String(result.feet));
      setInStr(String(Math.round(result.inches)));
      setDir("ftToCm");
    } else {
      setCmStr(String(Math.round(result.cm * 10) / 10));
      setDir("cmToFt");
    }
  };

  const pct = bdHeightPercentile(result.cm);

  return (
    <CalculatorLayout
      slug="cm-to-feet"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Ruler"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Direction toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-border bg-card p-1">
          {(["cmToFt", "ftToCm"] as Direction[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDir(d)}
              className={cn(
                "rounded-full px-4 py-1.5 text-xs font-medium transition-colors",
                dir === d ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                lang === "bn" && "font-bn",
              )}
            >
              {d === "cmToFt" ? tr("cmToFt") : tr("ftToCm")}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={swap}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:border-accent"
        >
          <ArrowLeftRight size={12} /> {tr("swap")}
        </button>
      </div>

      {/* Inputs */}
      <div className="mt-4 grid gap-4">
        {dir === "cmToFt" ? (
          <BigField
            label={tr("cm")}
            unit={lang === "en" ? "cm" : "সেমি"}
            value={cmStr}
            onChange={setCmStr}
            useBn={useBn}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <BigField label={tr("feet")} unit={lang === "en" ? "ft" : "ফুট"} value={ftStr} onChange={setFtStr} useBn={useBn} />
            <BigField label={tr("inches")} unit={lang === "en" ? "in" : "ইঞ্চি"} value={inStr} onChange={setInStr} useBn={useBn} />
          </div>
        )}
      </div>

      {/* Quick chips */}
      <div className="mt-4 flex flex-wrap gap-2">
        {QUICK_CHIPS.map((c) => (
          <button
            key={`${c.ft}-${c.in}`}
            type="button"
            onClick={() => {
              if (dir === "cmToFt") {
                setCmStr(String(Math.round((c.ft * 12 + c.in) * 2.54 * 10) / 10));
              } else {
                setFtStr(String(c.ft));
                setInStr(String(c.in));
              }
            }}
            className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-accent"
          >
            {num(c.ft)}'{num(c.in)}"
          </button>
        ))}
      </div>

      {/* Primary result */}
      <motion.div
        key={`${dir}-${cmStr}-${ftStr}-${inStr}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center"
      >
        <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
          {tr("result")}
        </p>
        {dir === "cmToFt" ? (
          <>
            <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">{fmtFtIn(result)}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {fmt(parseFloat(cmStr) || 0, 1)} {lang === "en" ? "cm" : "সেমি"} = {fmtFtInPrecise(result)}
            </p>
          </>
        ) : (
          <>
            <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
              {fmt(result.cm, 2)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? "cm" : "সেমি"}</span>
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {fmtFtIn(result)} = {fmt(result.cm, 2)} {lang === "en" ? "cm" : "সেমি"}
            </p>
          </>
        )}
        <div className="mt-4 grid gap-2 text-xs sm:grid-cols-4">
          <Stat label={lang === "en" ? "Decimal feet" : "দশমিক ফুট"} value={fmt(result.decimalFeet, 2)} />
          <Stat label={lang === "en" ? "Total inches" : "মোট ইঞ্চি"} value={fmt(result.totalInches, 2)} />
          <Stat label={lang === "en" ? "Meters" : "মিটার"} value={fmt(result.meters, 3)} />
          <Stat label={lang === "en" ? "BD percentile" : "বাংলাদেশে শতাংশিক"} value={`${num(pct)}%`} />
        </div>
      </motion.div>

      {/* Reference: averages */}
      <section className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Bangladesh average height" : "বাংলাদেশের গড় উচ্চতা"}
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            <li>♂ {lang === "en" ? "Male" : "পুরুষ"}: {fmt(BD_AVG.male.cm, 1)} {lang === "en" ? "cm" : "সেমি"} = {fmtFtIn(cmToFeetInches(BD_AVG.male.cm))}</li>
            <li>♀ {lang === "en" ? "Female" : "নারী"}: {fmt(BD_AVG.female.cm, 1)} {lang === "en" ? "cm" : "সেমি"} = {fmtFtIn(cmToFeetInches(BD_AVG.female.cm))}</li>
          </ul>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Global average height" : "বিশ্বের গড় উচ্চতা"}
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            <li>♂ {lang === "en" ? "Male" : "পুরুষ"}: {fmt(GLOBAL_AVG.male.cm, 0)} {lang === "en" ? "cm" : "সেমি"} = {fmtFtIn(cmToFeetInches(GLOBAL_AVG.male.cm))}</li>
            <li>♀ {lang === "en" ? "Female" : "নারী"}: {fmt(GLOBAL_AVG.female.cm, 0)} {lang === "en" ? "cm" : "সেমি"} = {fmtFtIn(cmToFeetInches(GLOBAL_AVG.female.cm))}</li>
          </ul>
        </div>
      </section>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "CM to feet conversion formula" : "সেমি থেকে ফুটে রূপান্তরের সূত্র"}
          </h2>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "1 inch = 2.54 cm (exact)." : "১ ইঞ্চি = ২.৫৪ সেমি (সঠিক)।"}</li>
            <li>{lang === "en" ? "1 foot = 12 inches = 30.48 cm." : "১ ফুট = ১২ ইঞ্চি = ৩০.৪৮ সেমি।"}</li>
            <li>{lang === "en" ? "Total inches = cm ÷ 2.54." : "মোট ইঞ্চি = সেমি ÷ ২.৫৪।"}</li>
            <li>{lang === "en" ? "Feet = floor(total inches ÷ 12); inches = remainder." : "ফুট = floor(মোট ইঞ্চি ÷ ১২); ইঞ্চি = অবশিষ্ট।"}</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Where you'll need this in Bangladesh" : "বাংলাদেশে কোথায় দরকার হয়"}
          </h2>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Passport application — height in cm." : "পাসপোর্ট ফরম — উচ্চতা সেন্টিমিটারে।"}</li>
            <li>{lang === "en" ? "NID & Smart Card — cm." : "NID ও স্মার্ট কার্ড — সেমি।"}</li>
            <li>{lang === "en" ? "Marriage biodata / Patro-Patri profile — usually feet & inches." : "বিয়ের বায়োডাটা — সাধারণত ফুট-ইঞ্চিতে।"}</li>
            <li>{lang === "en" ? "Gym / sports — cm or feet, depends on coach." : "জিম / খেলাধুলা — সেমি বা ফুট, কোচের উপর নির্ভর।"}</li>
            <li>{lang === "en" ? "Medical reports / BMI calculations — cm." : "মেডিকেল রিপোর্ট / BMI — সেমি।"}</li>
          </ul>
        </section>

        {/* CM → Ft table */}
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Height table — CM to feet & inches" : "উচ্চতা সারণি — সেমি থেকে ফুট ইঞ্চি"}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "CM" : "সেমি"}</th>
                  <th className="p-2 text-left">{lang === "en" ? "Feet & inches" : "ফুট ইঞ্চি"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Decimal ft" : "দশমিক ফুট"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Meters" : "মিটার"}</th>
                </tr>
              </thead>
              <tbody>
                {HEIGHT_TABLE_CM.map((c) => {
                  const r = cmToFeetInches(c);
                  return (
                    <tr key={c} className="border-t border-border">
                      <td className="p-2 tabular-nums">{num(c)}</td>
                      <td className="p-2 tabular-nums">{fmtFtIn(r)} <span className="text-xs text-muted-foreground">({fmtFtInPrecise(r)})</span></td>
                      <td className="p-2 text-right tabular-nums">{fmt(r.decimalFeet, 2)}</td>
                      <td className="p-2 text-right tabular-nums">{fmt(r.meters, 2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Ft → CM table */}
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Reverse table — feet & inches to cm" : "বিপরীত সারণি — ফুট ইঞ্চি থেকে সেমি"}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Feet & inches" : "ফুট ইঞ্চি"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "CM" : "সেমি"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Meters" : "মিটার"}</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const rows: { ft: number; in: number }[] = [];
                  for (let f = 4; f <= 7; f++) {
                    for (let i = 0; i < 12; i++) {
                      if (f === 7 && i > 0) continue;
                      rows.push({ ft: f, in: i });
                    }
                  }
                  return rows.map((r) => {
                    const fi = feetInchesToCm(r.ft, r.in);
                    return (
                      <tr key={`${r.ft}-${r.in}`} className="border-t border-border">
                        <td className="p-2 tabular-nums">{num(r.ft)}'{num(r.in)}"</td>
                        <td className="p-2 text-right tabular-nums">{fmt(fi.cm, 2)}</td>
                        <td className="p-2 text-right tabular-nums">{fmt(fi.meters, 2)}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"}
          </h2>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/bmi-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              BMI
            </Link>
            <Link to="/age-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Age" : "বয়স"}
            </Link>
            <Link to="/square-feet-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Square feet" : "বর্গফুট"}
            </Link>
            <Link to="/land-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Land" : "জমি"}
            </Link>
          </div>
        </section>
      </article>
    </CalculatorLayout>
  );
}

function BigField({
  label, unit, value, onChange, useBn,
}: { label: string; unit: string; value: string; onChange: (v: string) => void; useBn: boolean }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-muted-foreground">{label}</label>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          autoFocus
          value={useBn ? toBengaliNumerals(value) : value}
          onChange={(e) => onChange(stripBn(e.target.value))}
          className="w-full rounded-xl border border-border bg-card px-4 py-4 pr-16 text-2xl font-semibold tabular-nums focus:border-accent focus:outline-none"
        />
        <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm text-muted-foreground">
          {unit}
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}
